import {
  Users,
  Package,
  MapPin,
  Clock,
  AlertTriangle,
  Boxes,
  UserX,
} from 'lucide-react'
import { RolePageData } from '../types'

export const hrData: RolePageData = {
  id: 'hr',
  slug: 'hr',
  label: 'Human Resources',
  icon: Users,
  title: 'Make employees feel welcomed and valued',
  description:
    'Streamline onboarding, anniversaries, and culture initiatives without the admin headache.',

  context:
    'You own onboarding, culture, and internal events. Merch is one of many responsibilities — and often not by choice.',

  jobToBeDone:
    'Make employees feel welcomed and valued without creating admin hell.',

  dailyReality: [
    'Remote and hybrid employees scattered across locations',
    'Many addresses to manage for shipments',
    'Recurring moments like onboarding and anniversaries',
    'Little time and little merch expertise',
  ],

  painPoints: [
    {
      title: 'Logistics are painful',
      description:
        'Coordinating shipments to dozens of addresses, tracking deliveries, and handling returns takes more time than it should.',
      icon: Package,
    },
    {
      title: 'Inconsistent employee experience',
      description:
        'Some employees get great welcome kits, others get forgotten. The experience varies too much.',
      icon: UserX,
    },
    {
      title: 'Storage and coordination chaos',
      description:
        'Storing boxes in the office or coordinating shipments from suppliers creates ongoing overhead.',
      icon: Boxes,
    },
    {
      title: 'Fear of visible mistakes',
      description:
        'Messing up something visible to employees reflects badly on you and the company culture.',
      icon: AlertTriangle,
    },
  ],

  hiddenFears: [
    'This will reflect badly on me internally',
    'Employees will think this is cheap',
    "I'll be stuck chasing suppliers",
  ],

  esgAngle: {
    level: 'high',
    description:
      'High sensitivity to sustainability. Wants defensible choices, cares about waste, quality, and longevity.',
  },

  whatTheyValue: ['Predictability', 'Consistency', 'Support', 'Repeatable systems'],

  languageTheyUse: [
    'Onboarding',
    'Employee experience',
    'Culture',
    'Consistency',
    'Smooth process',
    'Low effort',
  ],

  convincers: [
    {
      quote: 'No logistics for you',
      description:
        'We handle all shipping, tracking, and delivery coordination. You just approve.',
    },
    {
      quote: 'Predefined kits ready to go',
      description:
        'Choose from curated onboarding and milestone kits that work every time.',
    },
    {
      quote: 'We guide you through everything',
      description:
        'From product selection to delivery, you have a dedicated team supporting you.',
    },
    {
      quote: 'Nothing moves without your approval',
      description:
        'Full control over what gets sent, when, and to whom. No surprises.',
    },
  ],

  dealBreakers: [
    {
      title: 'Feels like a webshop',
      description:
        "If it feels like we're just another catalog to browse, we've failed you.",
    },
    {
      title: 'Unclear responsibility',
      description:
        "You need to know exactly who's accountable when things go wrong.",
    },
    {
      title: 'Lack of human support',
      description:
        "Chatbots and self-service alone won't cut it when you need real help.",
    },
  ],

  testimonial: {
    headline: 'Finally, onboarding kits that actually arrive on time',
    name: 'Emma Lindberg',
    title: 'Head of People, TechFlow',
    image:
      'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop&crop=face',
  },

  faqs: [
    {
      question: 'How do you handle shipping to multiple employee addresses?',
      answer:
        'We manage individual shipments to any address you provide. Upload a list or integrate with your HR system, and we handle the rest — tracking, delivery confirmation, and any issues that arise.',
    },
    {
      question: 'Can we set up recurring orders for onboarding?',
      answer:
        'Yes! You can create predefined kits that are ready to ship whenever a new employee joins. Just trigger the order and we take care of everything.',
    },
    {
      question: 'What if an employee has a problem with their order?',
      answer:
        "We handle all customer service directly. Employees can reach out to us for replacements, size exchanges, or any issues — you don't have to be the middleman.",
    },
    {
      question: 'How sustainable are your products?',
      answer:
        'We prioritize eco-certified suppliers and materials. Every product includes sustainability information so you can make informed, defensible choices.',
    },
  ],

  cta: {
    primary: 'Get a clear onboarding kit proposal',
    secondary: 'See how onboarding merch works',
    promptTemplate:
      "Hi! I'm looking for an onboarding kit for new employees. We have remote and hybrid teams, and I want something that feels premium and welcoming. Can you help me put together a proposal?",
  },
}
